import { useMemo } from "react";
import { useDroppable } from "@dnd-kit/core";
import { SortableContext, type SortingStrategy } from "@dnd-kit/sortable";
import type { BoardCellEntry } from "./boardCells";
import { BoardCellList, type BoardCellListProps } from "./BoardCellList";
import {
    BoardCollapsedColumn,
    type BoardCollapsedColumnProps,
} from "./BoardCollapsedColumn";
import { getCellDroppableId, getStripDroppableId } from "./boardSensors";

// Slots move in the rendered order; nothing is displaced by transform.
const KEEP_IN_PLACE: SortingStrategy = () => null;

const getPlacementKeys = <TItem,>(
    entries: ReadonlyArray<BoardCellEntry<TItem>>,
) => entries.map((entry) => entry.placementKey);

/**
 * A cell on a draggable board: the whole cell accepts drops, so empty cells
 * and the space under the last card are targets too. The sortable ids cover
 * every entry, mounted or not.
 */
export const BoardDropCell = <TItem,>({
    entries,
    ...listProps
}: Readonly<Omit<BoardCellListProps<TItem>, "ref">>) => {
    const cellDroppableId = getCellDroppableId(listProps.placement);
    const { setNodeRef } = useDroppable({ id: cellDroppableId });
    const placementKeys = useMemo(() => getPlacementKeys(entries), [entries]);

    return (
        <SortableContext
            id={cellDroppableId}
            items={placementKeys}
            strategy={KEEP_IN_PLACE}
        >
            <BoardCellList ref={setNodeRef} entries={entries} {...listProps} />
        </SortableContext>
    );
};

interface BoardDropStripProps extends Omit<BoardCollapsedColumnProps, "ref"> {
    columnId: string;
}

/** A collapsed column strip that accepts drops. */
export const BoardDropStrip = ({
    columnId,
    ...stripProps
}: Readonly<BoardDropStripProps>) => {
    const { setNodeRef } = useDroppable({ id: getStripDroppableId(columnId) });
    return <BoardCollapsedColumn ref={setNodeRef} {...stripProps} />;
};
